import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  Typography,
  keyframes,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import { ACCENT_COLORS, BORDER_COLORS } from '../../utils/colors';
import { COUNTDOWN } from '../../constants/config';
import { useCountdown } from '../../hooks';

/**
 * Плавающая карточка уведомлений — правый нижний угол главной, видна на любой прокрутке.
 *
 * Внутри КАРУСЕЛЬ: первый слайд — отсчёт до запуска сервера, дальше акции. Слайды меняются сами раз в
 * 10 секунд, точки внизу переключают вручную, наведение мыши паузит смену (иначе карточка уезжает
 * из-под курсора, пока человек тянется к кнопке). Один слайд — точек и автосмены нет.
 *
 * **Добавить акцию = дописать объект в `PROMOS` ниже и блок текстов в `home.json` трёх локалей.**
 * Больше ничего трогать не надо: и точки, и автосмена считаются от длины списка.
 *
 * Зачем виджет: отсчёт из геро-блока виден только на первом экране, а решение «зайти» вызревает
 * ниже — на осадах, карте и экономике. Карточка держит и срок, и акции под рукой всю страницу.
 * С `md` и выше отсчёт живёт ТОЛЬКО здесь: из центра геро-блока его убрали, чтобы одни и те же
 * цифры не стояли на экране дважды.
 *
 * ТОЛЬКО ДЕСКТОП (от `md`). На телефоне карточка забирала пол-экрана и дралась за низ с
 * cookie-баннером и кнопкой «наверх» — заказчик попросил там её не показывать вовсе. Гасим
 * через `useMediaQuery`, а не `display: none`, чтобы на мобильном не тикал секундный таймер.
 * Ниже `md` отсчёт остаётся в центре геро-блока (`HeroSection` → `HeroCountdown`).
 *
 * Размеров у карточки ДВА — обычный и компактный (`REGULAR` / `COMPACT` ниже).
 *
 * Стоит в самом углу, а кнопка «наверх» (`BackToTopButton`) отодвигается влево и встаёт с ним
 * в один ряд — как именно, расписано у `anchor` ниже.
 *
 * Свёрнутое состояние — круг того же вида, что кнопка «наверх» и рейл соцсетей (`SocialRail`):
 * три плавающих элемента на одной странице, разъезжаться им нельзя. Выбор НЕ запоминается:
 * жил в localStorage, но свёрнутый раз виджет больше не показывался никогда — а он про
 * запуск сервера и должен напоминать о себе. Теперь сворачивание живёт только до перезагрузки.
 *
 * Цифры отсчёта берутся из общего `useCountdown` (тот же `COUNTDOWN` в `constants/config`, что и
 * в геро-блоке): до часа X — сколько осталось, после — сколько сервер уже работает. Отсчёт выключен или
 * час X позади при отключённом счёте вверх — слайд с ним просто выпадает из карусели.
 */

/**
 * Слайды-акции. Порядок = порядок показа, `id` = ключ блока текстов в `home.json`
 * (`launchWidget.promos.<id>` → `label`, `value`, `caption`, `text`, `cta`).
 *
 * `to` — куда ведёт кнопка. Кабинетные адреса (`/dashboard/*`) гостя отправят на вход, это
 * нормально: пополнять всё равно из-под аккаунта.
 */
// Пусто с 06.10.2026: акцию «+10% к пополнению» (`donateBonus` → `/dashboard/payment`) сняли —
// карточка снова из одного слайда-отсчёта.
const PROMOS: { id: string; to: string }[] = [];

/** Каждые сколько миллисекунд слайд меняется сам. */
const ROTATE_MS = 10_000;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeSwap = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/**
 * Общая для карточки и круга привязка к углу.
 *
 * Виджет и кнопка «наверх» стоят В ОДИН РЯД по нижней линии, а не столбиком: карточка в самом
 * углу, стрелка — слева от неё. Столбиком они читались как два случайно упавших рядом элемента.
 *
 * Двигается при этом СТРЕЛКА, а не карточка: свою ширину виджет отдаёт наверх через `onFootprint`,
 * а `HomePage` пересчитывает отступ у `BackToTopButton`. Так карточка стоит намертво в углу, а
 * стрелка сама доезжает на место, когда виджет свернули или развернули.
 *
 * Отступ от краёв (32) один на оба размера карточки: `HomePage` считает по нему позицию стрелки,
 * и разъехаться им нельзя.
 */
const anchor = {
  position: 'fixed' as const,
  right: 32,
  bottom: 32,
  zIndex: 1200,
};

/**
 * Когда карточка переключается в компактный размер.
 *
 * Ширина окна — НЕ единственный признак маленького экрана: ноутбучные 1366×768 и 1440×900 по
 * ширине проходят за полноценный десктоп, а по высоте карточка съедает на них заметную часть
 * окна и упирается в cookie-баннер. Поэтому условий два и хватает любого. Порог по высоте взят
 * с запасом на хром браузера: на 1080p окно остаётся выше 860px и карточка там обычная.
 */
const COMPACT_QUERY = '(max-width: 1279.98px), (max-height: 859.98px)';

/**
 * Размеры карточки. Всё, что меняется между обычной и компактной, живёт ЗДЕСЬ и только здесь —
 * ни один кегль и отступ ниже не прописан числом мимо этой таблицы. Иначе при следующей правке
 * два размера разъедутся: одно место поправят, второе забудут.
 *
 * Пропорция важнее абсолютных чисел: карточка должна читаться широким прямоугольником, а не
 * почти квадратом — поэтому она шире, чем была (320), и заметно ниже. Ширина подобрана так,
 * чтобы описание слайда укладывалось в ДВЕ строки: третья строка — плюс ~17px к высоте и
 * ровно то, из-за чего карточка выглядела громоздкой.
 *
 * `slideMinHeight` прибит, чтобы карточка не прыгала при смене слайда: у отсчёта и у акции
 * контент разной высоты, а скачущий на 20px угол экрана читается как баг. Появится слайд
 * выше — поднять число здесь, а не подгонять текст.
 */
interface CardSize {
  /** Ширина развёрнутой карточки — её же виджет отдаёт наверх через `onFootprint`. */
  width: number;
  /** Диаметр свёрнутого круга — отдаётся туда же. */
  circle: number;
  /** Внутренний отступ карточки, px. */
  pad: number;
  /** Кегль имени сервера в шапке. */
  brand: string;
  /** Отступ под шапкой, px. */
  headGap: number;
  /** Кегль подписи над цифрами. */
  label: string;
  /** Кегль самих цифр. */
  number: string;
  /** Кегль подписи под цифрой («дн», «ч», «мин»). */
  unit: string;
  /** Зазор между цифрой и её подписью в строке, px. */
  unitGap: string;
  /** Минимальная ширина блока одной цифры с подписью, px. */
  cellWidth: number;
  /** Зазор между колонками цифр, px. */
  cellGap: number;
  /** Отступ под строкой цифр, px. */
  clockGap: number;
  /** Кегль описания под цифрами. */
  text: string;
  /** Высота области слайда, px. */
  slideMinHeight: number;
  /** Отступ под областью слайда, px. */
  slideGap: number;
  /** Высота кнопок, px. */
  button: number;
  /** Кегль подписи в кнопке. */
  buttonText: string;
  /** Отступ над точками-переключателями, px. */
  dotsGap: number;
}

const REGULAR: CardSize = {
  width: 344,
  circle: 52,
  pad: 14,
  brand: '0.85rem',
  headGap: 10,
  label: '0.58rem',
  number: '1.45rem',
  unit: '0.55rem',
  unitGap: '5px',
  cellWidth: 74,
  cellGap: 14,
  clockGap: 10,
  text: '0.78rem',
  slideMinHeight: 104,
  slideGap: 12,
  button: 32,
  buttonText: '0.65rem',
  dotsGap: 10,
};

const COMPACT: CardSize = {
  width: 296,
  circle: 46,
  pad: 12,
  brand: '0.78rem',
  headGap: 8,
  label: '0.53rem',
  number: '1.2rem',
  unit: '0.5rem',
  unitGap: '4px',
  cellWidth: 64,
  cellGap: 12,
  clockGap: 8,
  text: '0.72rem',
  slideMinHeight: 92,
  slideGap: 10,
  button: 30,
  buttonText: '0.62rem',
  dotsGap: 8,
};

const labelSx = (s: CardSize) => ({
  color: 'rgba(255,255,255,0.6)',
  fontSize: s.label,
  fontWeight: 600,
  letterSpacing: '0.2em',
  textTransform: 'uppercase' as const,
  textAlign: 'center' as const,
  mb: 0.5,
});

const bigNumberSx = (s: CardSize) => ({
  color: '#fff',
  fontSize: s.number,
  fontWeight: 700,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums' as const,
});

const unitSx = (s: CardSize) => ({
  color: 'rgba(255,255,255,0.55)',
  fontSize: s.unit,
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase' as const,
  // Подпись стоит СБОКУ от цифры, а не под ней: колонка «цифра над подписью» добавляла карточке
  // ~16px высоты — ровно те, из-за которых угол экрана читался почти квадратом. Зазор — числом
  // из таблицы, а не шагом сетки: такая же карточка на лендинге набрана теми же пикселями, и
  // разница даже в полпикселя видна, когда два окна сравнивают рядом.
  ml: s.unitGap,
});

const textSx = (s: CardSize) => ({
  color: 'rgba(255,255,255,0.7)',
  fontSize: s.text,
  lineHeight: 1.4,
});

/**
 * Высота кнопок прибита, а не отдана содержимому: у слайдов разное число кнопок и разный текст,
 * ряд получался то 35px, то 33px, карточка прижата к низу — и её верхняя грань дёргалась на два
 * пикселя при каждой смене слайда. Мелочь, но глаз ловит.
 */
const primaryButtonSx = (s: CardSize) => ({
  flex: 1,
  height: s.button,
  minHeight: s.button,
  px: 1,
  /* Оптическое centering: подпись набрана капсом, выносных элементов вниз у неё нет,
     а строка резервирует место под хвосты «у», «р», «д» — из-за этого видимые буквы
     уезжают вверх на ~1px от середины кнопки. Опускаем их обратно асимметричным
     отступом: разница между верхом и низом ровно 2px, на любой высоте кнопки. */
  pt: '12px',
  pb: '10px',
  background: ACCENT_COLORS.GRADIENT_DEFAULT,
  color: '#fff',
  fontSize: s.buttonText,
  fontWeight: 600,
  letterSpacing: '0.02em',
  textTransform: 'uppercase' as const,
  borderRadius: '8px',
  transition: 'all 0.25s ease',
  '&:hover': {
    background: ACCENT_COLORS.GRADIENT_HOVER,
    boxShadow: `0 0 18px ${ACCENT_COLORS.GLOW}`,
  },
});

/**
 * Золотая обводка карточки — просьба заказчика: карточка должна читаться как подарок,
 * а не как ещё одна панель интерфейса. Золото берём из палитры проекта (`#f59e0b`,
 * тот же янтарь, что у статусной точки до старта), а не подбираем на глаз.
 *
 * Гилдится ТОЛЬКО развёрнутая карточка. Свёрнутый круг остаётся нейтральным: он одного
 * вида с кнопкой «наверх» и рейлом соцсетей, и трём плавающим элементам на одной
 * странице разъезжаться нельзя.
 */
const GOLD_BORDER = 'rgba(245, 158, 11, 0.45)';
const GOLD_GLOW = 'rgba(245, 158, 11, 0.18)';

/** Слайд отсчёта: дни/часы/минуты — без секунд, чтобы карточка не дёргалась всю прокрутку. */
const CountdownSlide: React.FC<{
  size: CardSize;
  live: boolean;
  days: number;
  hours: number;
  minutes: number;
  startLabel: string;
}> = ({ size, live, days, hours, minutes, startLabel }) => {
  const { t } = useTranslation('home');

  return (
    <>
      {/* Подпись — те же строки, что в геро-блоке, чтобы формулировки не разъезжались */}
      <Typography sx={labelSx(size)}>
        {live ? t('hero.countdown.uptimeTitle') : t('hero.countdown.title')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: `${size.cellGap}px`,
          justifyContent: 'center',
          mb: `${size.clockGap}px`,
        }}
      >
        {[
          { value: days, label: t('hero.countdown.days') },
          { value: hours, label: t('hero.countdown.hours') },
          { value: minutes, label: t('hero.countdown.minutes') },
        ].map((item) => (
          <Box
            key={item.label}
            /* По базовой линии, а не по центру: иначе мелкая подпись всплывает над серединой цифры
               и строка выглядит кривой. */
            sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', minWidth: size.cellWidth }}
          >
            <Typography sx={bigNumberSx(size)}>{String(item.value).padStart(2, '0')}</Typography>
            <Typography sx={unitSx(size)}>{item.label}</Typography>
          </Box>
        ))}
      </Box>

      <Typography sx={textSx(size)}>
        {live
          ? t('launchWidget.liveSince', { date: startLabel })
          : t('launchWidget.startsAt', { date: startLabel })}
      </Typography>
    </>
  );
};

/** Слайд акции: те же крупные цифры, что у отсчёта, чтобы карусель читалась как одна карточка. */
const PromoSlide: React.FC<{ size: CardSize; id: string }> = ({ size, id }) => {
  const { t } = useTranslation('home');
  const key = `launchWidget.promos.${id}`;

  return (
    <>
      <Typography sx={labelSx(size)}>{t(`${key}.label`)}</Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'center',
          mb: `${size.clockGap}px`,
        }}
      >
        <Typography sx={{ ...bigNumberSx(size), color: ACCENT_COLORS.PRIMARY_LIGHT }}>
          {t(`${key}.value`)}
        </Typography>
        <Typography sx={unitSx(size)}>{t(`${key}.caption`)}</Typography>
      </Box>

      <Typography sx={textSx(size)}>{t(`${key}.text`)}</Typography>
    </>
  );
};

interface LaunchWidgetProps {
  /**
   * Сколько места виджет занимает у правого края (0 — его на экране нет).
   * `HomePage` двигает по этому числу кнопку «наверх», чтобы они стояли в ряд, а не друг на друге.
   */
  onFootprint?: (width: number) => void;
}

const LaunchWidget: React.FC<LaunchWidgetProps> = ({ onFootprint }) => {
  const { t, i18n } = useTranslation('home');
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const isCompact = useMediaQuery(COMPACT_QUERY, { noSsr: true });
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });
  const [collapsed, setCollapsed] = useState(false);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const time = useCountdown();

  const size = isCompact ? COMPACT : REGULAR;
  const countdownOn = COUNTDOWN.ENABLED && !time.finished;
  const visible = isDesktop && (countdownOn || PROMOS.length > 0);
  const slideCount = (countdownOn ? 1 : 0) + PROMOS.length;

  useEffect(() => {
    if (!onFootprint) return;
    onFootprint(visible ? (collapsed ? size.circle : size.width) : 0);
  }, [onFootprint, visible, collapsed, size]);

  // Автосмена. `index` в зависимостях намеренно: переключил точкой — отсчёт 10 секунд начинается
  // заново, иначе слайд мог смениться сразу после ручного выбора. При `prefers-reduced-motion`
  // карусель не крутится сама: движение без спроса — ровно то, от чего этот режим защищает.
  useEffect(() => {
    if (slideCount < 2 || paused || reducedMotion) return undefined;
    const id = setInterval(() => setIndex((prev) => (prev + 1) % slideCount), ROTATE_MS);
    return () => clearInterval(id);
  }, [slideCount, paused, reducedMotion, index]);

  // Список слайдов схлопнулся (акция кончилась, отсчёт отработал) — не показываем пустоту.
  useEffect(() => {
    if (index >= slideCount) setIndex(0);
  }, [index, slideCount]);

  // Дата в подписи — из того же TARGET_DATE, а не строкой в переводах: меняется дата запуска,
  // и подпись едет за ней сама. Часовой пояс прибит к Москве — время анонсируется в МСК.
  const startLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Moscow',
      }).format(new Date(COUNTDOWN.TARGET_DATE)),
    [i18n.language],
  );

  if (!visible) return null;

  const live = time.countingUp;
  const current = Math.min(index, slideCount - 1);
  const promo = countdownOn ? PROMOS[current - 1] : PROMOS[current];
  const onCountdownSlide = countdownOn && current === 0;

  if (collapsed) {
    // Анимация появления живёт на ОБЁРТКЕ, а не на самой кнопке, и это не вкусовщина:
    // `animation … both` держит `transform` элемента после проигрывания, а анимация в каскаде
    // перебивает обычные правила — ховерный `translateY(-3px)` на кнопке просто не срабатывал,
    // кружок стоял столбом, пока стрелка «наверх» рядом подпрыгивала. Разнесли по двум элементам:
    // обёртка выезжает, кнопка прыгает на наведении, как и её соседка.
    //
    // Всплывающей подписи на ховере нет намеренно — заказчик просил убрать. `aria-label` остаётся:
    // он для скринридеров и на экране не показывается.
    return (
      <Box sx={{ ...anchor, lineHeight: 0, animation: `${slideIn} 0.4s ease-out both` }}>
        <IconButton
          onClick={() => setCollapsed(false)}
          aria-label={t('launchWidget.expand')}
          sx={{
            width: size.circle,
            height: size.circle,
            background: 'rgba(0, 0, 0, 0.6)',
            border: `1px solid ${BORDER_COLORS.LIGHT}`,
            color: 'rgba(255, 255, 255, 0.75)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            transition: 'all 0.25s ease',
            '&:hover': {
              background: ACCENT_COLORS.BG_LIGHT,
              borderColor: ACCENT_COLORS.PRIMARY,
              color: ACCENT_COLORS.PRIMARY,
              boxShadow: `0 0 20px ${ACCENT_COLORS.GLOW}, 0 8px 24px rgba(0,0,0,0.5)`,
              transform: 'translateY(-3px)',
            },
          }}
        >
          <AccessTimeIcon sx={{ fontSize: isCompact ? '1.25rem' : '1.5rem' }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{
        ...anchor,
        width: size.width,
        p: `${size.pad}px`,
        background: 'rgba(10, 10, 10, 0.72)',
        backdropFilter: 'blur(16px)',
        border: `1px solid ${GOLD_BORDER}`,
        borderRadius: '14px',
        boxShadow: `0 16px 40px rgba(0,0,0,0.55), 0 0 24px ${GOLD_GLOW}`,
        animation: `${slideIn} 0.5s ease-out both`,
      }}
    >
      {/* Шапка: имя сервера + «свернуть». Одна на все слайды — меняется только содержимое
          ниже, иначе карусель распадалась бы на разные карточки.

          Мигающей статусной точки слева от имени здесь БОЛЬШЕ НЕТ — 08.09.2026 заказчик
          попросил убрать её и с сайта, и с лендинга. Понадобится вернуть — это осознанное
          решение: на первом экране и без неё хватает движения. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: `${size.headGap}px` }}>
        <Typography
          sx={{
            flex: 1,
            color: '#fff',
            fontSize: size.brand,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          Odium World
        </Typography>
        <IconButton
          onClick={() => setCollapsed(true)}
          aria-label={t('launchWidget.collapse')}
          size="small"
          sx={{
            color: 'rgba(255,255,255,0.5)',
            '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.08)' },
          }}
        >
          <CloseIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Box>

      {/* Область слайда. `key` заставляет содержимое перерисоваться с проявлением, а фиксированная
          минимальная высота держит карточку на месте при смене. */}
      <Box
        key={onCountdownSlide ? 'countdown' : promo?.id}
        sx={{
          minHeight: size.slideMinHeight,
          mb: `${size.slideGap}px`,
          animation: `${fadeSwap} 0.35s ease-out both`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        {onCountdownSlide ? (
          <CountdownSlide
            size={size}
            live={live}
            days={time.days}
            hours={time.hours}
            minutes={time.minutes}
            startLabel={startLabel}
          />
        ) : (
          promo && <PromoSlide size={size} id={promo.id} />
        )}
      </Box>

      {/* Ряд кнопок уходит в ТОТ ЖЕ кросс-фейд, что и содержимое слайда, и по тому же
          ключу. Раньше анимация была только на области слайда, а кнопки лежали снаружи:
          подпись менялась мгновенно («Забрать набор» → «Пополнить»), пока всё над ней
          плавно проявлялось, и это читалось как подскок текста в кнопке. */}
      <Box
        key={onCountdownSlide ? 'actions-countdown' : `actions-${promo?.id}`}
        sx={{
          display: 'flex',
          gap: 1,
          animation: `${fadeSwap} 0.35s ease-out both`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        {onCountdownSlide ? (
          // Одна кнопка вместо прежних двух («Начать играть» + «Создать аккаунт»):
          // заказчик попросил вести отсюда прямо к стартовым наборам. Гостя адрес
          // кабинета отправит на вход — это нормально, покупать всё равно из-под аккаунта.
          <Button component={Link} to="/dashboard/shop" sx={primaryButtonSx(size)}>
            {t('launchWidget.pack')}
          </Button>
        ) : (
          promo && (
            <Button component={Link} to={promo.to} sx={primaryButtonSx(size)}>
              {t(`launchWidget.promos.${promo.id}.cta`)}
            </Button>
          )
        )}
      </Box>

      {/* Точки-переключатели. Одного слайда мало для карусели — тогда их нет вовсе. */}
      {slideCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: `${size.dotsGap}px` }}>
          {Array.from({ length: slideCount }, (_, i) => (
            <Box
              key={i}
              component="button"
              onClick={() => setIndex(i)}
              aria-label={t('launchWidget.goToSlide', { number: i + 1 })}
              aria-current={i === current}
              sx={{
                width: i === current ? 16 : 6,
                height: 6,
                p: 0,
                border: 0,
                borderRadius: '3px',
                cursor: 'pointer',
                background: i === current ? ACCENT_COLORS.PRIMARY : 'rgba(255,255,255,0.25)',
                transition: 'all 0.25s ease',
                '&:hover': { background: i === current ? ACCENT_COLORS.PRIMARY : 'rgba(255,255,255,0.5)' },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default LaunchWidget;
